import { Action } from '../../models/core/core.type';
import { ProposalType } from '../../models/core/proposals.type';
import { SessionStorageKey } from '../../utils/constants.utils';
import { formatDateStringToDate } from '../../utils/formatter.utils';
import { tryGetDateISOFormat } from '../../utils/helper.utils';

type ProposalDraftData = Partial<ProposalType> & {
  residuos?: any[];
  servicos?: any[];
  comissoes?: any[];
};

type ProposalDraftStorageType = {
  proposal: ProposalDraftData;
  savedAt: string | null;
  mode: 'create' | 'edit';
  idProposta: number | null;
};

type ProposalDraftSummaryItemType = {
  label: string;
  add: number;
  modify: number;
  delete: number;
};

const getProposalDraftStorageKey = (idProposta?: number | null) =>
  `${SessionStorageKey.TempProposal}:${idProposta || 'new'}`;

const hydrateProposalDraftData = (proposal: ProposalDraftData): ProposalDraftData => ({
  ...proposal,
  dataInicial: proposal.dataInicial
    ? formatDateStringToDate(tryGetDateISOFormat(proposal.dataInicial as any))
    : undefined,
  dataFinal: proposal.dataFinal
    ? formatDateStringToDate(tryGetDateISOFormat(proposal.dataFinal as any))
    : undefined,
  validade: proposal.validade
    ? formatDateStringToDate(tryGetDateISOFormat(proposal.validade as any))
    : undefined,
});

const loadProposalDraft = (idProposta?: number | null): ProposalDraftStorageType | null => {
  const storageKey = getProposalDraftStorageKey(idProposta);
  const rawDraft = sessionStorage.getItem(storageKey);
  const legacyDraft = !idProposta ? sessionStorage.getItem(SessionStorageKey.TempProposal) : null;
  const serialized = rawDraft || legacyDraft;

  if (!serialized) {
    return null;
  }

  const parsed = JSON.parse(serialized);
  if (parsed?.proposal) {
    return {
      ...parsed,
      proposal: hydrateProposalDraftData(parsed.proposal),
    };
  }

  return {
    proposal: hydrateProposalDraftData(parsed),
    savedAt: null,
    mode: idProposta ? 'edit' : 'create',
    idProposta: idProposta || null,
  };
};

const saveProposalDraft = (proposal: ProposalDraftData, idProposta?: number | null) => {
  const storageKey = getProposalDraftStorageKey(idProposta);
  const data: ProposalDraftStorageType = {
    proposal,
    savedAt: new Date().toISOString(),
    mode: idProposta ? 'edit' : 'create',
    idProposta: idProposta || null,
  };

  sessionStorage.setItem(storageKey, JSON.stringify(data));
  if (!idProposta) {
    sessionStorage.removeItem(SessionStorageKey.TempProposal);
  }
};

const removeProposalDraft = (idProposta?: number | null) => {
  sessionStorage.removeItem(getProposalDraftStorageKey(idProposta));
  if (!idProposta) {
    sessionStorage.removeItem(SessionStorageKey.TempProposal);
  }
};

const countActions = (items: any[] = []) =>
  items.reduce(
    (acc, item) => {
      switch (item?.action) {
        case Action.Add:
          acc.add += 1;
          break;
        case Action.Modify:
          acc.modify += 1;
          break;
        case Action.Delete:
          acc.delete += 1;
          break;
        default:
          break;
      }

      return acc;
    },
    { add: 0, modify: 0, delete: 0 }
  );

const buildProposalDraftSummary = (proposal: ProposalDraftData): ProposalDraftSummaryItemType[] => {
  const residuos = proposal.residuos || [];
  const servicos = proposal.servicos || [];
  const planos = residuos.flatMap((residuo) => residuo.planos || []);
  const cotacoesResiduo = planos.flatMap((plano) => plano.cotacoes || []);
  const cotacoesServico = servicos.flatMap((servico) => servico.cotacoes || []);

  return [
    { label: 'Resíduos', ...countActions(residuos) },
    { label: 'Planos de resíduo', ...countActions(planos) },
    { label: 'Cotações de resíduo', ...countActions(cotacoesResiduo) },
    { label: 'Serviços', ...countActions(servicos) },
    { label: 'Cotações de serviço', ...countActions(cotacoesServico) },
  ].filter((item) => item.add || item.modify || item.delete);
};

const mergeProposalWithDraft = (
  proposal: ProposalType | null,
  draft: ProposalDraftData | null
): ProposalType | null => {
  if (!draft) {
    return proposal;
  }

  if (!proposal) {
    return draft as ProposalType;
  }

  return {
    ...proposal,
    ...draft,
    residuos: draft.residuos || proposal.residuos,
    servicos: draft.servicos || proposal.servicos,
    comissoes: draft.comissoes || proposal.comissoes,
  } as ProposalType;
};

export {
  buildProposalDraftSummary,
  getProposalDraftStorageKey,
  loadProposalDraft,
  mergeProposalWithDraft,
  removeProposalDraft,
  saveProposalDraft,
};
export type { ProposalDraftData, ProposalDraftStorageType, ProposalDraftSummaryItemType };
